import React, { Fragment, useEffect, useState } from "react";
import style from "../scss/Login.module.scss";
import { getLogin, reenviarCodigo, verificarCodigo } from "../Api/autenticacion";
import { Icon } from "@iconify/react";
import AlertComponent from "./AlertComponent";
import { ACCESO_SUPERADMIN, guardarSesion, rutaInicio } from "../utils/sesion";
import { obtenerMensajeError } from "../utils/mensajeError";
import {
  biometriaDisponible,
  iniciarSesionConBiometria,
  leerCredencialLocal,
  mensajeErrorBiometria,
} from "../utils/biometria";

// Login en dos pasos: 1) usuario y contraseña, 2) código enviado al correo.
// Si el usuario activó la huella/rostro en este equipo (desde su perfil), puede entrar con ella.
// acceso: "superadmin" en el login privado del superadmin (el backend rechaza a los demás roles).
const LoginComponent = ({ acceso }) => {
  const esPortalSuperadmin = acceso === ACCESO_SUPERADMIN;
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [codigo, setCodigo] = useState("");
  const [verificacion, setVerificacion] = useState(null); // { tokenTemporal, correo, minutosVigencia }
  const [cargando, setCargando] = useState(false);
  const [alerta, setAlerta] = useState("");
  const [colorAlerta, setColorAlerta] = useState("alert-danger");
  const [mostraralert, setMostraralert] = useState(false);
  const [verPassword, setVerPassword] = useState(false);
  const [accesoBiometrico, setAccesoBiometrico] = useState(false);

  // el botón solo aparece si este equipo tiene lector y el acceso ya se activó aquí
  useEffect(() => {
    if (!leerCredencialLocal()) return;
    biometriaDisponible().then(setAccesoBiometrico);
  }, []);

  const mostrarAlerta = (mensaje, color = "alert-danger") => {
    setAlerta(mensaje);
    setColorAlerta(color);
    setMostraralert(true);
    setTimeout(() => setMostraralert(false), 4000);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setCargando(true);
    try {
      const { data } = await getLogin(username, password, acceso);

      // usuario con 2FA desactivado: el backend ya entrega la sesión
      if (!data.requiere2fa) {
        iniciarSesion(data);
        return;
      }

      setVerificacion(data);
      setCodigo("");
      mostrarAlerta(`Enviamos un código a ${data.correo}`, "alert-success");
      setCargando(false);
    } catch (error) {
      mostrarAlerta(obtenerMensajeError(error));
      setCargando(false);
    }
  };

  const iniciarSesion = (datosSesion) => {
    guardarSesion(datosSesion);
    // recarga completa para que axios tome el token nuevo (ver config.js)
    window.location.href = rutaInicio(datosSesion.rol);
  };

  const handleVerificar = async (event) => {
    event.preventDefault();
    setCargando(true);
    try {
      const { data } = await verificarCodigo(verificacion.tokenTemporal, codigo);
      iniciarSesion(data);
    } catch (error) {
      mostrarAlerta(obtenerMensajeError(error));
      setCargando(false);
    }
  };

  const handleLoginBiometria = async () => {
    setCargando(true);
    try {
      iniciarSesion(await iniciarSesionConBiometria(acceso));
    } catch (error) {
      mostrarAlerta(mensajeErrorBiometria(error));
      // el acceso de este equipo se desactivó desde otro dispositivo
      setAccesoBiometrico(Boolean(leerCredencialLocal()));
      setCargando(false);
    }
  };

  const handleReenviar = async () => {
    try {
      const { data } = await reenviarCodigo(verificacion.tokenTemporal);
      mostrarAlerta(data.msg, "alert-success");
    } catch (error) {
      mostrarAlerta(obtenerMensajeError(error));
    }
  };

  const volverAlLogin = () => {
    setVerificacion(null);
    setCodigo("");
    setPassword("");
  };

  return (
    <Fragment>
      <div className={style.alertaContenedor}>
        <AlertComponent
          mensaje={alerta}
          mostraralert={mostraralert}
          styleGrup={`${colorAlerta} text-center`}
        />
      </div>

      <div className={style.loginContainer}>
        <div className={style.loginLeft}>
          {!verificacion ? (
            <form className={style.login} onSubmit={handleLogin}>
              <img src="../../Img/logoElecciones.png" className={style.loginLogo} alt="logo" />
              <h2 className={style.titulo}>{esPortalSuperadmin ? "Administración del sistema" : "Iniciar sesión"}</h2>
              <p className={style.textoAyuda}>
                {esPortalSuperadmin
                  ? "Acceso exclusivo del superadministrador"
                  : "Ingresa tus credenciales para continuar"}
              </p>

              <div className="mb-3">
                <label htmlFor="usuario" className={style.etiqueta}>
                  Usuario
                </label>
                <div className={style.campo}>
                  <Icon icon="mdi:account-outline" className={style.campoIcono} aria-hidden="true" />
                  <input
                    type="text"
                    className={`form-control ${style.input}`}
                    id="usuario"
                    autoComplete="username"
                    placeholder="Tu usuario"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="password" className={style.etiqueta}>
                  Contraseña
                </label>
                <div className={style.campo}>
                  <Icon icon="mdi:lock-outline" className={style.campoIcono} aria-hidden="true" />
                  <input
                    type={verPassword ? "text" : "password"}
                    className={`form-control ${style.input} ${style.inputConAccion}`}
                    id="password"
                    autoComplete="current-password"
                    placeholder="Tu contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className={style.btnVerPassword}
                    onClick={() => setVerPassword(!verPassword)}
                    aria-label={verPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    <Icon icon={verPassword ? "mdi:eye-off-outline" : "mdi:eye-outline"} />
                  </button>
                </div>
              </div>

              <button type="submit" className={`btn btn-primary w-100 ${style.btnPrincipal}`} disabled={cargando}>
                {cargando ? "Enviando código..." : "Continuar"}
              </button>

              {accesoBiometrico && (
                <>
                  <div className={style.separador}>
                    <span>o</span>
                  </div>
                  <button
                    type="button"
                    className={`btn btn-outline-primary w-100 ${style.btnBiometria}`}
                    onClick={handleLoginBiometria}
                    disabled={cargando}
                  >
                    <Icon icon="mdi:fingerprint" aria-hidden="true" />
                    Ingresar con huella o rostro
                  </button>
                </>
              )}
            </form>
          ) : (
            <form className={style.login} onSubmit={handleVerificar}>
              <span className={style.iconoVerificacion} aria-hidden="true">
                <Icon icon="mdi:email-lock-outline" />
              </span>
              <h2 className={style.titulo}>Verificación</h2>
              <p className={style.textoAyuda}>
                Ingresa el código de 6 dígitos que enviamos a <strong>{verificacion.correo}</strong>.
                Vence en {verificacion.minutosVigencia} minutos.
              </p>

              <div className="mb-3">
                <label htmlFor="codigo" className={style.etiqueta}>
                  Código
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  pattern="\d{6}"
                  className={`form-control ${style.inputCodigo}`}
                  id="codigo"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
                  autoFocus
                  required
                />
              </div>

              <button
                type="submit"
                className={`btn btn-primary w-100 ${style.btnPrincipal}`}
                disabled={cargando || codigo.length !== 6}
              >
                {cargando ? "Verificando..." : "Verificar"}
              </button>

              <div className={style.accionesCodigo}>
                <button type="button" className="btn btn-link p-0" onClick={handleReenviar}>
                  Reenviar código
                </button>
                <button type="button" className="btn btn-link p-0" onClick={volverAlLogin}>
                  Cambiar usuario
                </button>
              </div>
            </form>
          )}
        </div>
        <div className={style.divider}></div>

        <div className={style.loginRight}></div>
      </div>
    </Fragment>
  );
};
export default LoginComponent;
